/**
 * Query Keys for React Query
 * 
 * Organisert hierarkisk for enkel invalidering:
 * - ['productions'] - alle produksjoner
 * - ['productions', id] - én produksjon
 * - ['productions', id, 'bemanning'] - bemanning for produksjon
 * 
 * Dette gjør det enkelt å invalidere:
 * - queryClient.invalidateQueries({ queryKey: ['productions'] }) - Alle produksjoner
 * - queryClient.invalidateQueries({ queryKey: ['productions', 1] }) - Én produksjon og alt underdata
 */

export const queryKeys = {
  // Produksjoner
  productions: {
    all: ['productions'] as const,
    lists: () => [...queryKeys.productions.all, 'list'] as const,
    list: (filters?: Record<string, any>) => [...queryKeys.productions.lists(), filters] as const,
    details: () => [...queryKeys.productions.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.productions.details(), id] as const,
    
    // Produksjon relatert data
    bemanning: (id: number) => [...queryKeys.productions.detail(id), 'bemanning'] as const,
    talentBehov: (id: number) => [...queryKeys.productions.detail(id), 'talentBehov'] as const,
    plan: (id: number) => [...queryKeys.productions.detail(id), 'plan'] as const,
    planElementer: (id: number) => [...queryKeys.productions.detail(id), 'planElementer'] as const,
    oppmote: (id: number) => [...queryKeys.productions.detail(id), 'oppmote'] as const,
    varslingsstatus: (id: number) => [...queryKeys.productions.detail(id), 'varslingsstatus'] as const,
  },
  
  // Brukere
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters?: Record<string, any>) => [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.users.details(), id] as const,
    talents: (userId: number) => [...queryKeys.users.detail(userId), 'talents'] as const,
    withTalents: (talentId?: number) => [...queryKeys.users.all, 'with-talents', talentId] as const,
  },
  
  // Talents
  talents: {
    all: ['talents'] as const,
    lists: () => [...queryKeys.talents.all, 'list'] as const,
    list: (filters?: Record<string, any>) => [...queryKeys.talents.lists(), filters] as const,
    details: () => [...queryKeys.talents.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.talents.details(), id] as const,
    tree: () => [...queryKeys.talents.all, 'tree'] as const,
  },
  
  // Produksjonsmaler
  productionCategories: {
    all: ['production-categories'] as const,
    lists: () => [...queryKeys.productionCategories.all, 'list'] as const,
    details: () => [...queryKeys.productionCategories.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.productionCategories.details(), id] as const,
    komplett: (id: number) => [...queryKeys.productionCategories.detail(id), 'komplett-mal'] as const,
  },
  
  // Produksjonsplaner
  productionPlans: {
    all: ['production-plans'] as const,
    details: () => [...queryKeys.productionPlans.all, 'detail'] as const,
    detail: (id: number) => [...queryKeys.productionPlans.details(), id] as const,
  },
  
  // Auth
  auth: {
    me: ['auth', 'me'] as const,
  },
} as const;

